import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  Plus,
  Trash2,
  AlertCircle,
  MessageSquare,
  Phone as PhoneIcon,
  Save,
  Sparkles,
  Zap,
  ScrollText,
  LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import agentRulesService, {
  AgentRule,
  AgentRuleCreate,
  AgentRuleUpdate,
  AgentRuleEvent,
} from '../services/agentRulesService';
import voiceService from '../services/voiceService';
import crossSellConfigService, {
  AddonConfig,
  AddonConfigPatch,
  AddonGender,
  CrossSellSite,
} from '../services/crossSellPreviewService';
import { CharCounter } from '@/components/ui/char-counter';
import type { BehaviorTab } from '../lib/agentDeepLink';
import {
  ADDON_NOTE_MAX,
  AGENT_RULE_TEXT_MAX,
  AGENT_RULE_TITLE_MAX,
  AGENT_RULES_MAX_ACTIVE,
} from '@/constants/contentLimits';

interface DraftRule {
  id?: number;
  title: string;
  rule_text: string;
  apply_voice: boolean;
  apply_whatsapp: boolean;
  enabled: boolean;
  // Leer = "immer aktiv"; nicht-leer = "proaktiv" (feuert bei diesen Events).
  trigger_events: string[];
  // null = die Regel gilt für alle Agenten des Unternehmens.
  phone_number: string | null;
  dirty: boolean;
}

const emptyDraft = (scopeToAgent: string | null): DraftRule => ({
  title: '',
  rule_text: '',
  apply_voice: true,
  apply_whatsapp: true,
  enabled: true,
  trigger_events: [],
  // A rule written while one agent is open belongs to that agent — the owner
  // can widen it to all agents with one click.
  phone_number: scopeToAgent,
  dirty: true,
});

const ruleToDraft = (r: AgentRule): DraftRule => ({
  id: r.id,
  title: r.title || '',
  rule_text: r.rule_text,
  apply_voice: r.apply_voice,
  apply_whatsapp: r.apply_whatsapp,
  enabled: r.enabled,
  trigger_events: r.trigger_events || [],
  phone_number: r.phone_number || null,
  dirty: false,
});

interface AgentBehaviorSectionProps {
  /** Agent whose behaviour is being configured. null = the company-wide set. */
  agentNumber?: string | null;
  /** Tab shown first (deep link `?sub=`). */
  initialTab?: BehaviorTab;
  /** The owner switched tabs. */
  onTabChange?: (tab: BehaviorTab) => void;
}

/** Markenblau — der einzige Farbakzent, auch auf dem aktiven Reiter. */
const ACCENT = '#1967D2';

/**
 * Verhalten teilt sich in zwei Reiter, damit die Seite nicht endlos scrollt:
 * Cross-Selling (Schalter + Zusatzleistungen) und die freien Anweisungen.
 */
const BEHAVIOR_TABS: { id: BehaviorTab; label: string; icon: LucideIcon }[] = [
  { id: 'crosssell', label: 'Up- & Cross-Selling', icon: Sparkles },
  { id: 'rules', label: 'Freie Anweisungen', icon: ScrollText },
];

const AgentBehaviorSection: React.FC<AgentBehaviorSectionProps> = ({
  agentNumber = null,
  initialTab = 'crosssell',
  onTabChange,
}) => {
  const [activeTab, setActiveTabState] = useState<BehaviorTab>(initialTab);
  const setActiveTab = (tab: BehaviorTab) => {
    setActiveTabState(tab);
    onTabChange?.(tab);
  };
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | 'new' | null>(null);
  const [drafts, setDrafts] = useState<DraftRule[]>([]);
  const [events, setEvents] = useState<AgentRuleEvent[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [crossSellEnabled, setCrossSellEnabled] = useState<boolean | null>(null);
  const [crossSellSaving, setCrossSellSaving] = useState<boolean>(false);
  const [crossSellError, setCrossSellError] = useState<string | null>(null);
  // Kunden-Anrede (du/sie): Verhalten beider Kanäle (Telefon + WhatsApp),
  // deshalb hier statt im Telefon-Tab. null = noch nicht geladen.
  const [salutation, setSalutation] = useState<'du' | 'sie' | null>(null);
  const [salutationSaving, setSalutationSaving] = useState<boolean>(false);
  const [salutationInherited, setSalutationInherited] = useState<boolean>(false);

  // --- Cross-Sell Add-On configuration state ---
  const [sites, setSites] = useState<CrossSellSite[]>([]);
  const [sitesLoading, setSitesLoading] = useState<boolean>(true);
  const [sitesError, setSitesError] = useState<string | null>(null);
  const [selectedSiteCd, setSelectedSiteCd] = useState<string>('');
  const [addons, setAddons] = useState<AddonConfig[]>([]);
  const [addonsLoading, setAddonsLoading] = useState<boolean>(false);
  const [addonsError, setAddonsError] = useState<string | null>(null);
  const [pendingNotes, setPendingNotes] = useState<Record<number, string>>({});
  const [savingItemNos, setSavingItemNos] = useState<Set<number>>(new Set());
  const notesSaveTimersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Rules of this agent plus the ones that apply to every agent.
        const rulesPromise = agentRulesService.list(agentNumber);
        const settingsPromise = voiceService.getSettings(agentNumber);
        const eventsPromise = agentRulesService.listEvents();
        const rules = await rulesPromise;
        if (cancelled) return;
        setDrafts(rules.map(ruleToDraft));
        setErrorMsg(null);
        // Event-Katalog ist optional: schlägt er fehl, bleibt der Picker leer,
        // der Modus-Badge funktioniert trotzdem aus den geladenen Regeln.
        eventsPromise
          .then((ev) => {
            if (!cancelled) setEvents(ev);
          })
          .catch(() => {
            /* Katalog optional — kein harter Fehler */
          });
        try {
          const voiceSettings = await settingsPromise;
          if (cancelled) return;
          if (voiceSettings && typeof voiceSettings.cross_sell_enabled === 'boolean') {
            setCrossSellEnabled(voiceSettings.cross_sell_enabled);
            setCrossSellError(null);
          } else {
            setCrossSellEnabled(null);
            setCrossSellError('Einstellung konnte nicht geladen werden.');
          }
          if (voiceSettings) {
            setSalutation(voiceSettings.salutation === 'sie' ? 'sie' : 'du');
            setSalutationInherited(Boolean(voiceSettings.inherited?.salutation));
          }
        } catch (settingsErr: any) {
          if (cancelled) return;
          setCrossSellEnabled(null);
          setCrossSellError(
            settingsErr?.message || 'Einstellung konnte nicht geladen werden.',
          );
        }
      } catch (e: any) {
        if (cancelled) return;
        setErrorMsg(e?.message || 'Regeln konnten nicht geladen werden.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Switching agents reloads its rules and its cross-sell state.
  }, [agentNumber]);

  // Load salons once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSitesLoading(true);
      try {
        const list = await crossSellConfigService.listSites();
        if (cancelled) return;
        setSites(list);
        setSitesError(null);
        if (list.length > 0) setSelectedSiteCd(list[0].siteCd);
      } catch (e: any) {
        if (cancelled) return;
        setSitesError(e?.message || 'Salons konnten nicht geladen werden.');
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentNumber]);

  // Load add-on configs whenever the salon changes.
  useEffect(() => {
    if (!selectedSiteCd) {
      setAddons([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setAddonsLoading(true);
      setPendingNotes({});
      try {
        const list = await crossSellConfigService.listAddons(selectedSiteCd);
        if (cancelled) return;
        setAddons(list);
        setAddonsError(null);
      } catch (e: any) {
        if (cancelled) return;
        setAddons([]);
        setAddonsError(e?.message || 'Zusatzleistungen konnten nicht geladen werden.');
      } finally {
        if (!cancelled) setAddonsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSiteCd]);

  // Clean up any pending debounced note-saves on unmount.
  useEffect(() => () => {
    Object.values(notesSaveTimersRef.current).forEach(clearTimeout);
  }, []);

  const groupedAddons = useMemo(() => {
    const groups = new Map<string, AddonConfig[]>();
    for (const addon of addons) {
      const key = addon.category || 'Sonstiges';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(addon);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b, 'de'));
  }, [addons]);

  const markSaving = (itemNo: number, on: boolean) => {
    setSavingItemNos((prev) => {
      const next = new Set(prev);
      if (on) next.add(itemNo);
      else next.delete(itemNo);
      return next;
    });
  };

  const persistAddonChange = async (itemNo: number, patch: AddonConfigPatch) => {
    if (!selectedSiteCd) return;
    markSaving(itemNo, true);
    try {
      const updated = await crossSellConfigService.upsertAddon(selectedSiteCd, itemNo, patch);
      setAddons((prev) => prev.map((a) => (a.itemNo === itemNo ? { ...a, ...updated } : a)));
    } catch (e: any) {
      toast.error(e?.message || 'Speichern fehlgeschlagen.');
    } finally {
      markSaving(itemNo, false);
    }
  };

  const handleToggleEnabled = (itemNo: number, enabled: boolean) => {
    setAddons((prev) => prev.map((a) => (a.itemNo === itemNo ? { ...a, enabled } : a)));
    persistAddonChange(itemNo, { enabled });
  };

  const handleGenderChange = (itemNo: number, gender: AddonGender) => {
    setAddons((prev) => prev.map((a) => (a.itemNo === itemNo ? { ...a, gender } : a)));
    persistAddonChange(itemNo, { gender });
  };

  // Notes are debounced — typing should feel fluent, not save-on-every-keystroke.
  const handleNotesChange = (itemNo: number, value: string) => {
    setPendingNotes((prev) => ({ ...prev, [itemNo]: value }));
    if (notesSaveTimersRef.current[itemNo]) {
      clearTimeout(notesSaveTimersRef.current[itemNo]);
    }
    notesSaveTimersRef.current[itemNo] = setTimeout(() => {
      delete notesSaveTimersRef.current[itemNo];
      const finalValue = value.trim();
      setAddons((prev) =>
        prev.map((a) => (a.itemNo === itemNo ? { ...a, notes: finalValue } : a)),
      );
      setPendingNotes((prev) => {
        const next = { ...prev };
        delete next[itemNo];
        return next;
      });
      persistAddonChange(itemNo, { notes: finalValue });
    }, 600);
  };

  const toggleCrossSell = async (next: boolean) => {
    const prev = crossSellEnabled;
    setCrossSellEnabled(next);
    setCrossSellSaving(true);
    try {
      const updated = await voiceService.updateSettings(
        { cross_sell_enabled: next },
        agentNumber,
      );
      const serverValue =
        typeof updated?.cross_sell_enabled === 'boolean'
          ? updated.cross_sell_enabled
          : next;
      setCrossSellEnabled(serverValue);
      setCrossSellError(null);
      toast.success(
        serverValue
          ? 'Up- und Cross-Selling aktiviert.'
          : 'Up- und Cross-Selling deaktiviert.',
      );
    } catch (e: any) {
      setCrossSellEnabled(prev);
      toast.error(e?.message || 'Konnte Einstellung nicht speichern.');
    } finally {
      setCrossSellSaving(false);
    }
  };

  const changeSalutation = async (next: 'du' | 'sie') => {
    const prev = salutation;
    if (next === prev) return;
    setSalutation(next);
    setSalutationSaving(true);
    try {
      const updated = await voiceService.updateSettings({ salutation: next }, agentNumber);
      setSalutation(updated?.salutation === 'sie' ? 'sie' : 'du');
      setSalutationInherited(false);
      toast.success(
        next === 'sie'
          ? 'Die Agenten siezen Kunden ab sofort.'
          : 'Die Agenten duzen Kunden ab sofort.',
      );
    } catch (e: any) {
      setSalutation(prev);
      toast.error(e?.message || 'Konnte Anrede nicht speichern.');
    } finally {
      setSalutationSaving(false);
    }
  };

  const updateDraft = (idx: number, patch: Partial<DraftRule>) => {
    setDrafts((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch, dirty: true };
      return next;
    });
  };

  const toggleEvent = (idx: number, key: string) => {
    setDrafts((prev) => {
      const next = [...prev];
      const cur = next[idx];
      const trigger_events = cur.trigger_events.includes(key)
        ? cur.trigger_events.filter((k) => k !== key)
        : [...cur.trigger_events, key];
      next[idx] = { ...cur, trigger_events, dirty: true };
      return next;
    });
  };

  // Die Liste enthält genau das, was DIESER Agent sieht (unternehmensweite
  // Anweisungen plus seine eigenen) — also ist das auch die Zahl, die gegen das
  // Limit läuft. Deaktivierte zählen nicht: sie gehen nicht in den Prompt.
  const activeRuleCount = drafts.filter((d) => d.enabled).length;
  const ruleLimitReached = activeRuleCount >= AGENT_RULES_MAX_ACTIVE;

  const validateDraft = (d: DraftRule): string | null => {
    if (!d.rule_text.trim()) return 'Regeltext darf nicht leer sein.';
    if (d.rule_text.trim().length > AGENT_RULE_TEXT_MAX) {
      return `Der Regeltext darf höchstens ${AGENT_RULE_TEXT_MAX} Zeichen haben.`;
    }
    if (!d.apply_voice && !d.apply_whatsapp) {
      return 'Mindestens ein Kanal (Telefon oder WhatsApp) muss aktiv sein.';
    }
    return null;
  };

  const saveDraft = async (idx: number) => {
    const d = drafts[idx];
    const validation = validateDraft(d);
    if (validation) {
      toast.error(validation);
      return;
    }

    setSavingId(d.id ?? 'new');
    try {
      if (d.id == null) {
        const payload: AgentRuleCreate = {
          title: d.title.trim() || null,
          rule_text: d.rule_text.trim(),
          apply_voice: d.apply_voice,
          apply_whatsapp: d.apply_whatsapp,
          enabled: d.enabled,
          trigger_events: d.trigger_events,
          phone_number: d.phone_number || undefined,
        };
        const created = await agentRulesService.create(payload);
        setDrafts((prev) => prev.map((p, i) => (i === idx ? ruleToDraft(created) : p)));
        toast.success('Regel gespeichert.');
      } else {
        const payload: AgentRuleUpdate = {
          title: d.title.trim() || null,
          rule_text: d.rule_text.trim(),
          apply_voice: d.apply_voice,
          apply_whatsapp: d.apply_whatsapp,
          enabled: d.enabled,
          trigger_events: d.trigger_events,
          // "" moves the rule back to "gilt für alle Agenten".
          phone_number: d.phone_number ?? '',
        };
        const updated = await agentRulesService.update(d.id, payload);
        setDrafts((prev) => prev.map((p, i) => (i === idx ? ruleToDraft(updated) : p)));
        toast.success('Regel aktualisiert.');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSavingId(null);
    }
  };

  const removeDraft = async (idx: number) => {
    const d = drafts[idx];
    if (d.id == null) {
      setDrafts((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    if (!window.confirm('Diese Regel wirklich löschen?')) return;
    setSavingId(d.id);
    try {
      await agentRulesService.remove(d.id);
      setDrafts((prev) => prev.filter((_, i) => i !== idx));
      toast.success('Regel gelöscht.');
    } catch (e: any) {
      toast.error(e?.message || 'Löschen fehlgeschlagen.');
    } finally {
      setSavingId(null);
    }
  };

  const addDraft = () => {
    if (ruleLimitReached) {
      toast.error(
        `Mehr als ${AGENT_RULES_MAX_ACTIVE} aktive Anweisungen sind pro Agent nicht möglich. ` +
          'Fasse verwandte Anweisungen zusammen oder deaktiviere eine nicht mehr benötigte.',
      );
      return;
    }
    setDrafts((prev) => [...prev, emptyDraft(agentNumber)]);
  };

  // Roving-Tabindex: Pfeiltasten wandern durch die Reiter, wie man es gewohnt ist.
  const tablistRef = useRef<HTMLDivElement>(null);
  const handleTabKeyDown = (e: React.KeyboardEvent, index: number) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;
    e.preventDefault();
    const next = (index + step + BEHAVIOR_TABS.length) % BEHAVIOR_TABS.length;
    setActiveTab(BEHAVIOR_TABS[next].id);
    tablistRef.current
      ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
      ?.[next]?.focus();
  };

  return (
    <div className="space-y-6">
      {errorMsg && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{errorMsg}</AlertDescription>
        </Alert>
      )}

      {/* Kunden-Anrede: gilt kanalübergreifend, deshalb VOR den Reitern. */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
          <div className="space-y-1">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-[#1967D2]" />
              Anrede der Kunden
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Gilt für Telefon- <span className="font-medium">und</span> WhatsApp-Agent
              sowie Erinnerungen. Bei „Sie" bleibt der Agent auch dann förmlich, wenn der
              Kunde duzt. Eigene Begrüßungs- und Verabschiedungstexte bleiben unverändert —
              bitte selbst in der passenden Anrede formulieren.
            </p>
            {agentNumber && salutationInherited && (
              <p className="text-xs text-muted-foreground">
                Standard des Unternehmens — gilt für alle Agenten, bis du ihn hier für
                diesen Agenten überschreibst.
              </p>
            )}
          </div>
          <div className="pt-1 w-44 shrink-0">
            <Select
              value={salutation ?? undefined}
              onValueChange={(v) => changeSalutation(v === 'sie' ? 'sie' : 'du')}
              disabled={loading || salutationSaving || salutation === null}
            >
              <SelectTrigger aria-label="Anrede der Kunden" className="bg-background">
                <SelectValue placeholder="Lädt…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="du" className="cursor-pointer">Du (Standard)</SelectItem>
                <SelectItem value="sie" className="cursor-pointer">Sie</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
      </Card>

      {/* Reiter innerhalb "Verhalten": die lange Seite in zwei Bereiche geteilt. */}
      <div
        ref={tablistRef}
        role="tablist"
        aria-label="Verhalten-Bereiche"
        className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 p-1"
      >
        {BEHAVIOR_TABS.map((tab, index) => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`behavior-subtab-${tab.id}`}
              aria-selected={isActive}
              aria-controls={`behavior-subpanel-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(e) => handleTabKeyDown(e, index)}
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1967D2]/40',
                isActive
                  ? 'text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              style={isActive ? { backgroundColor: ACCENT } : undefined}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
              {tab.id === 'rules' && drafts.length > 0 && (
                <span
                  className={cn(
                    'inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold',
                    isActive ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground',
                  )}
                >
                  {drafts.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab === 'crosssell' && (
        <div
          role="tabpanel"
          id="behavior-subpanel-crosssell"
          aria-labelledby="behavior-subtab-crosssell"
          className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300"
        >
          <Card>
            <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
              <div className="space-y-1">
                <CardTitle className="text-base">Up- und Cross-Selling</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Wenn aktiviert, bietet der Agent bei jeder kompatiblen Buchung
                  automatisch eine passende Zusatzleistung an
                  (z. B. Kopfmassage zum Haarschnitt). Maximal zwei Vorschläge pro
                  Buchung, nie mehrfach pro Termin. Gilt für Telefon- und WhatsApp-Agent.
                </p>
                {crossSellError && (
                  <p className="text-xs text-destructive-strong">{crossSellError}</p>
                )}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Switch
                  id="cross-sell-enabled"
                  checked={crossSellEnabled === true}
                  onCheckedChange={toggleCrossSell}
                  disabled={loading || crossSellSaving || crossSellEnabled === null}
                />
                <Label htmlFor="cross-sell-enabled" className="text-sm font-normal min-w-[3rem]">
                  {crossSellEnabled === null
                    ? 'Lädt…'
                    : crossSellEnabled
                      ? 'Aktiv'
                      : 'Aus'}
                </Label>
              </div>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#1967D2]" />
                Zusatzleistungen für Cross-Selling
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Stelle ein, welche Zusatzleistungen der Agent vorschlagen darf, für wen sie passen
                und gib bei Bedarf einen Hinweis mit. Der Agent wählt im Gespräch 1–2 davon, die zur
                Hauptbuchung passen.
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              {sitesError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{sitesError}</AlertDescription>
                </Alert>
              )}

              {sitesLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Salons werden geladen…
                </div>
              ) : sites.length === 0 && !sitesError ? (
                <p className="text-sm text-muted-foreground">
                  Es wurden keine Salons gefunden. Bitte prüfe deine TimeGlobe-Anbindung im Onboarding.
                </p>
              ) : (
                <>
                  {sites.length > 1 && (
                    <div className="space-y-2">
                      <Label htmlFor="cross-sell-site">Salon</Label>
                      <Select value={selectedSiteCd} onValueChange={setSelectedSiteCd}>
                        <SelectTrigger id="cross-sell-site" className="bg-background">
                          <SelectValue placeholder="Salon wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {sites.map((s) => (
                            <SelectItem key={s.siteCd} value={s.siteCd}>
                              {s.siteNm}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {addonsError && (
                    <p className="text-xs text-destructive-strong">{addonsError}</p>
                  )}
                  {addonsLoading ? (
                    <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Zusatzleistungen werden geladen…
                    </div>
                  ) : addons.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Für diesen Salon sind keine Zusatzleistungen hinterlegt.
                    </p>
                  ) : (
                    <div className="space-y-5">
                      {groupedAddons.map(([category, items]) => (
                        <div key={category} className="space-y-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {category}
                          </p>
                          <div className="rounded-md border border-border divide-y divide-border bg-card">
                            {items.map((addon) => {
                              const saving = savingItemNos.has(addon.itemNo);
                              const notesValue =
                                pendingNotes[addon.itemNo] !== undefined
                                  ? pendingNotes[addon.itemNo]
                                  : addon.notes;
                              return (
                                <div
                                  key={addon.itemNo}
                                  className={cn(
                                    'p-3 space-y-2.5 transition-opacity',
                                    !addon.enabled && 'bg-muted/60',
                                  )}
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                      <Switch
                                        checked={addon.enabled}
                                        onCheckedChange={(v) =>
                                          handleToggleEnabled(addon.itemNo, v === true)
                                        }
                                        aria-label={`${addon.name} aktiviert`}
                                      />
                                      <div className="min-w-0">
                                        <p
                                          className={cn(
                                            'text-sm font-medium text-foreground truncate',
                                            !addon.enabled && 'text-muted-foreground line-through',
                                          )}
                                        >
                                          {addon.name}
                                        </p>
                                        {addon.price && (
                                          <p className="text-xs text-muted-foreground">{addon.price}</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground/70" />}
                                      <Select
                                        value={addon.gender}
                                        onValueChange={(v) =>
                                          handleGenderChange(addon.itemNo, v as AddonGender)
                                        }
                                        disabled={!addon.enabled}
                                      >
                                        <SelectTrigger className="w-[120px] h-8 bg-background text-xs">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="any">Alle</SelectItem>
                                          <SelectItem value="female">Damen</SelectItem>
                                          <SelectItem value="male">Herren</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                  <Input
                                    value={notesValue}
                                    onChange={(e) => handleNotesChange(addon.itemNo, e.target.value)}
                                    placeholder='Notiz für den Agenten (optional, z.B. „nur nach Färbung")'
                                    maxLength={ADDON_NOTE_MAX}
                                    disabled={!addon.enabled}
                                    className="bg-background text-sm"
                                  />
                                  {notesValue.length > ADDON_NOTE_MAX * 0.6 && (
                                    <CharCounter value={notesValue} max={ADDON_NOTE_MAX} />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'rules' && (
        <div
          role="tabpanel"
          id="behavior-subpanel-rules"
          aria-labelledby="behavior-subtab-rules"
          className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-base font-semibold text-foreground">Freie Anweisungen</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                Jede Regel wird in den System-Prompt eingehängt und gilt – je nach
                Auswahl – für den Telefon-Agenten, den WhatsApp-Agenten oder beide.
                Weniger und kürzer ist besser: der Agent befolgt ein paar klare
                Sätze zuverlässiger als lange Fließtexte.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={cn(
                  'text-xs tabular-nums',
                  ruleLimitReached
                    ? 'font-medium text-amber-600 dark:text-amber-500'
                    : 'text-muted-foreground/70',
                )}
              >
                {activeRuleCount} von {AGENT_RULES_MAX_ACTIVE} aktiv
              </span>
              <Button
                onClick={addDraft}
                disabled={loading || ruleLimitReached}
                variant="outline"
                title={
                  ruleLimitReached
                    ? `Limit erreicht: höchstens ${AGENT_RULES_MAX_ACTIVE} aktive Anweisungen je Agent.`
                    : undefined
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                Neue Regel
              </Button>
            </div>
          </div>

          {ruleLimitReached && !loading && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Das Limit von {AGENT_RULES_MAX_ACTIVE} aktiven Anweisungen ist
                erreicht. Um Platz zu schaffen: verwandte Anweisungen zu einer
                zusammenfassen oder eine nicht mehr benötigte deaktivieren.
              </AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground/70" />
            </div>
          ) : drafts.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                Noch keine Regeln angelegt. Klicke oben auf{' '}
                <strong>„Neue Regel"</strong>, um zu starten.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {drafts.map((d, idx) => {
                const busy = savingId === d.id || (d.id == null && savingId === 'new');
                return (
                  <Card key={d.id ?? `new-${idx}`} className={!d.enabled ? 'opacity-60' : ''}>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0">
                      <CardTitle className="text-base">
                        {d.id == null ? 'Neue Regel' : `Regel #${d.id}`}
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={d.enabled}
                            onCheckedChange={(v) => updateDraft(idx, { enabled: v })}
                            disabled={busy}
                            id={`enabled-${idx}`}
                          />
                          <Label htmlFor={`enabled-${idx}`} className="text-sm font-normal">
                            Aktiv
                          </Label>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeDraft(idx)}
                          disabled={busy}
                          aria-label="Regel löschen"
                        >
                          <Trash2 className="h-4 w-4 text-destructive-strong" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor={`title-${idx}`}>Titel (optional)</Label>
                        <Input
                          id={`title-${idx}`}
                          value={d.title}
                          onChange={(e) => updateDraft(idx, { title: e.target.value })}
                          placeholder="z.B. Friseurempfehlung Sommer"
                          maxLength={AGENT_RULE_TITLE_MAX}
                          disabled={busy}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`text-${idx}`}>Regeltext</Label>
                        <Textarea
                          id={`text-${idx}`}
                          value={d.rule_text}
                          onChange={(e) => updateDraft(idx, { rule_text: e.target.value })}
                          placeholder="z.B. „Erwähne bei Buchungen die aktuelle Aktion: Kopfmassage gratis ab 50€."
                          rows={4}
                          maxLength={AGENT_RULE_TEXT_MAX}
                          disabled={busy}
                        />
                        <CharCounter
                          value={d.rule_text}
                          max={AGENT_RULE_TEXT_MAX}
                          hint="Ein Gedanke pro Anweisung — knapp formuliert wirkt sie am zuverlässigsten."
                        />
                      </div>

                      {/* Scope: only meaningful once the tenant runs several
                          agents — otherwise every rule trivially applies to all. */}
                      {agentNumber && (
                        <div className="flex items-center gap-2 pt-1">
                          <Switch
                            id={`scope-${idx}`}
                            checked={d.phone_number == null}
                            onCheckedChange={(v) =>
                              updateDraft(idx, { phone_number: v ? null : agentNumber })
                            }
                            disabled={busy}
                          />
                          <Label
                            htmlFor={`scope-${idx}`}
                            className="text-sm font-normal cursor-pointer"
                          >
                            {d.phone_number == null
                              ? 'Gilt für alle Agenten'
                              : 'Gilt nur für diesen Agenten'}
                          </Label>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-6 pt-1">
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`voice-${idx}`}
                            checked={d.apply_voice}
                            onCheckedChange={(v) =>
                              updateDraft(idx, v ? { apply_voice: true } : { apply_voice: false, trigger_events: [] })
                            }
                            disabled={busy}
                          />
                          <Label
                            htmlFor={`voice-${idx}`}
                            className="text-sm font-normal flex items-center gap-1.5 cursor-pointer"
                          >
                            <PhoneIcon className="h-3.5 w-3.5" />
                            Telefon-Agent
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`wa-${idx}`}
                            checked={d.apply_whatsapp}
                            onCheckedChange={(v) => updateDraft(idx, { apply_whatsapp: v })}
                            disabled={busy}
                          />
                          <Label
                            htmlFor={`wa-${idx}`}
                            className="text-sm font-normal flex items-center gap-1.5 cursor-pointer"
                          >
                            <MessageSquare className="h-3.5 w-3.5" />
                            WhatsApp-Agent
                          </Label>
                        </div>
                      </div>

                      {d.apply_voice && (
                      <div className="space-y-2 pt-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Label className="text-sm font-normal flex items-center gap-1.5">
                            <Zap className="h-3.5 w-3.5" />
                            Telefon-Momente
                          </Label>
                          {d.trigger_events.length === 0 ? (
                            <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                              Immer aktiv
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-[#1967D2]/10 px-2 py-0.5 text-xs font-medium text-[#1967D2]">
                              Nur Telefon · {d.trigger_events.length}{' '}
                              {d.trigger_events.length === 1 ? 'Moment' : 'Momente'}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Ohne Auswahl gilt die Regel dauerhaft. Wählst du Momente, bekommt der
                          Telefon-Agent die Anweisung genau in diesem Moment. Momente gibt es nur
                          beim Telefon-Agent – WhatsApp nutzt immer die dauerhafte Regel.
                        </p>
                        {events.length > 0 && (
                          <div className="flex flex-wrap gap-2 pt-0.5">
                            {events.map((ev) => {
                              const active = d.trigger_events.includes(ev.key);
                              return (
                                <button
                                  type="button"
                                  key={ev.key}
                                  onClick={() => toggleEvent(idx, ev.key)}
                                  disabled={busy}
                                  aria-pressed={active}
                                  className={cn(
                                    'rounded-full border px-3 py-1 text-xs transition-colors disabled:opacity-50',
                                    active
                                      ? 'border-[#1967D2] bg-[#1967D2]/10 text-[#1967D2]'
                                      : 'border-border text-muted-foreground hover:border-border hover:bg-muted',
                                  )}
                                >
                                  {ev.label}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      )}

                      <div className="flex justify-end pt-2">
                        <Button
                          onClick={() => saveDraft(idx)}
                          disabled={busy || !d.dirty}
                        >
                          {busy ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="mr-2 h-4 w-4" />
                          )}
                          Speichern
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentBehaviorSection;
